import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { Controller, useForm } from "react-hook-form";
import IntlCurrencyInput from "react-intl-currency-input";
import { useClients } from "../../../hooks/useClients";
import { useLawsuits } from "../../../hooks/useLawsuits";

export const HONORARIO_TYPES = [
  "Inicial",
  "Mensal",
  "Êxito",
  "Sucumbencial",
  "Consultoria",
];

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function buildPayload(values) {
  const lawsuitId =
    values.lawsuit_id && String(values.lawsuit_id) !== "0"
      ? values.lawsuit_id
      : null;

  return {
    client_id: values.client_id,
    lawsuit_id: lawsuitId,
    type: values.type,
    contracted_value: Number(values.contracted_value) || 0,
    received_value: Number(values.received_value) || 0,
  };
}

export default function FormHonorario({
  show,
  onHide,
  honorario,
  onSubmit,
  isSubmitting,
}) {
  const isEdit = Boolean(honorario?.id);
  const { clients, isLoading: isLoadingClients } = useClients(undefined);
  const { lawsuits, isFetching: isLoadingLawsuits } = useLawsuits(undefined);
  const [contractedAmount, setContractedAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);

  const { register, handleSubmit, reset, control, watch } = useForm({
    defaultValues: {
      client_id: "",
      lawsuit_id: "",
      type: "Inicial",
      contracted_value: 0,
      received_value: 0,
    },
  });

  const watchedContracted = watch("contracted_value");
  const watchedReceived = watch("received_value");
  const liveSaldo =
    (Number(watchedContracted) || 0) - (Number(watchedReceived) || 0);

  useEffect(() => {
    if (!show) return;

    if (honorario) {
      const contracted = Number(honorario.contracted_value) || 0;
      const received = Number(honorario.received_value) || 0;
      setContractedAmount(contracted);
      setReceivedAmount(received);
      reset({
        client_id: honorario.client_id ? String(honorario.client_id) : "",
        lawsuit_id: honorario.lawsuit_id ? String(honorario.lawsuit_id) : "",
        type: honorario.type || "Inicial",
        contracted_value: contracted,
        received_value: received,
      });
      return;
    }

    setContractedAmount(0);
    setReceivedAmount(0);
    reset({
      client_id: "",
      lawsuit_id: "",
      type: "Inicial",
      contracted_value: 0,
      received_value: 0,
    });
  }, [show, honorario, reset]);

  const submit = async (values) => {
    await onSubmit(buildPayload(values));
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg">
      <Modal.Header closeButton className="modalGradient">
        <Modal.Title className="text-white">
          {isEdit ? "Editar honorário" : "Novo honorário"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(submit)}>
          <Form.Group className="mb-3">
            <FloatingLabel label="Cliente">
              <Form.Select
                {...register("client_id", { required: true })}
                disabled={isLoadingClients}
              >
                <option value="">Selecione o cliente</option>
                {clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Processo (opcional)">
              <Form.Select
                {...register("lawsuit_id")}
                disabled={isLoadingLawsuits}
              >
                <option value="">Sem processo</option>
                {lawsuits?.map((lawsuit) => (
                  <option key={lawsuit.id} value={lawsuit.id}>
                    {lawsuit.number}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Tipo">
              <Form.Select {...register("type", { required: true })}>
                {HONORARIO_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <FloatingLabel label="Valor contratado">
                <Controller
                  control={control}
                  name="contracted_value"
                  rules={{ required: true, min: 0.01 }}
                  render={({ field }) => (
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      className="form-control"
                      value={contractedAmount}
                      onChange={(event, value) => {
                        setContractedAmount(value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FloatingLabel>
            </div>
            <div className="col-md-6">
              <FloatingLabel label="Valor recebido">
                <Controller
                  control={control}
                  name="received_value"
                  render={({ field }) => (
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      className="form-control"
                      value={receivedAmount}
                      onChange={(event, value) => {
                        setReceivedAmount(value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FloatingLabel>
            </div>
          </div>

          <div className="mb-3 p-3 rounded border bg-light">
            <small className="text-muted d-block">Saldo</small>
            <strong
              className={liveSaldo < 0 ? "text-danger" : "text-dark"}
              style={{ fontSize: "1.15rem" }}
            >
              {formatCurrency(liveSaldo)}
            </strong>
            <Form.Text className="text-muted d-block mt-1">
              Calculado em tempo real: contratado − recebido.
            </Form.Text>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" type="button" onClick={onHide}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="btn-bg-primary text-dark borderColor"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Salvando...
                </>
              ) : isEdit ? (
                "Salvar"
              ) : (
                "Cadastrar"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
